import removeAccents from 'remove-accents';
import { getPerformerAndVenueForEvent } from 'src/utils/eventWithDataUtils';
import {
  CatalogResults,
  Event,
  EventWithData,
  Performer,
  Venue,
} from 'src/WebApiController';

export type ScopedEventFilters = {
  searchText?: string | null;
  performerId?: number | null;
  venueId?: number | null;
  /** When true, "parking passes only" events are excluded (mirroring the
   *  EventSearch parking filter), which also drops performers/venues whose
   *  only scoped events are parking events. */
  excludeParkingEvents?: boolean;
  /** When true, pending/rejected (unmapped) events are excluded (mirroring
   *  the EventSearch pending/rejected filter). */
  excludePendingRejectedEvents?: boolean;
};

export const isParkingEvent = (name: string | null | undefined): boolean =>
  (name ?? '').toLowerCase().startsWith('parking passes only');

export const isPendingRejectedEvent = (event: Event): boolean =>
  event.viagId == null;

const normalizeText = (text: string | null | undefined): string =>
  removeAccents(text ?? '').toLocaleUpperCase();

/**
 * Word-prefix matching, mirroring server autocomplete semantics: every
 * whitespace-separated search token must match the start of some word in
 * the target name. So "ed" matches "Ed Sheeran" but not "Red Wolves" or
 * "Comedy Club", and "ed sh" still matches "Ed Sheeran".
 */
export const matchesSearchText = (
  name: string | null | undefined,
  searchText: string | null | undefined
): boolean => {
  const tokens = normalizeText(searchText)
    .split(/\s+/)
    .filter((token) => token.length > 0);
  if (tokens.length === 0) {
    return true;
  }
  const words = normalizeText(name)
    .split(/\s+/)
    .filter((word) => word.length > 0);
  return tokens.every((token) => words.some((word) => word.startsWith(token)));
};

/**
 * Filters a scoped event list (passed to EventSearch via `eventsToSearchFrom`)
 * by search text and/or performer/venue selection.
 *
 * - Text-only search: matches on the event name (same normalization as the
 *   legacy inline filter in EventSearch).
 * - Performer/venue filter: resolves each event's performer/venue through the
 *   ambient catalog and keeps only events belonging to the selected entity.
 *   The search text is ignored in this case because it carries the entity
 *   name, not the event name.
 */
export const filterScopedEvents = (
  events: EventWithData[],
  {
    searchText,
    performerId,
    venueId,
    excludeParkingEvents,
    excludePendingRejectedEvents,
  }: ScopedEventFilters,
  catalog?: CatalogResults | null
): EventWithData[] => {
  const hasEntityFilter = performerId != null || venueId != null;

  if (!hasEntityFilter) {
    return events.filter(
      (e) =>
        !(excludeParkingEvents && isParkingEvent(e.event.name)) &&
        !(excludePendingRejectedEvents && isPendingRejectedEvent(e.event)) &&
        matchesSearchText(e.event.name, searchText)
    );
  }

  if (catalog == null) {
    return [];
  }

  return events.filter((e) => {
    if (excludeParkingEvents && isParkingEvent(e.event.name)) {
      return false;
    }
    if (excludePendingRejectedEvents && isPendingRejectedEvent(e.event)) {
      return false;
    }
    const { performer, venue } = getPerformerAndVenueForEvent(e.event, catalog);
    if (performerId != null && performer?.viagId !== performerId) {
      return false;
    }
    if (venueId != null && venue?.viagId !== venueId) {
      return false;
    }
    return true;
  });
};

/**
 * Derives performer/venue autocomplete results for a scoped search from the
 * full scoped event list. Only performers/venues whose own name matches the
 * search text are returned (mirroring server-side autocomplete semantics,
 * where each result section matches the typed text independently).
 */
export const deriveScopedPerformersAndVenues = (
  events: EventWithData[],
  searchText: string | null | undefined,
  catalog?: CatalogResults | null
): { performers: Record<string, Performer>; venues: Record<string, Venue> } => {
  const performers: Record<string, Performer> = {};
  const venues: Record<string, Venue> = {};

  if (catalog == null) {
    return { performers, venues };
  }

  events.forEach((e) => {
    const { performer, venue } = getPerformerAndVenueForEvent(e.event, catalog);
    if (
      performer?.viagId != null &&
      matchesSearchText(performer.name, searchText)
    ) {
      const key = performer.viagId.toString();
      performers[key] ??= performer;
    }
    if (
      venue?.viagId != null &&
      !venue.excludeSearch &&
      matchesSearchText(venue.name, searchText)
    ) {
      const key = venue.viagId.toString();
      venues[key] ??= venue;
    }
  });

  return { performers, venues };
};
